/**
 * 导出按钮组件
 *
 * 支持多层导出（L1/L2/L3/L4）
 * 支持多格式（TXT/MD/JSON/SVG/XMind）
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download,
  FileText,
  FileJson,
  Image,
  Share2,
  Check,
  Loader2,
} from 'lucide-react';
import type {
  GeneratedOutline,
  VolumeOutline,
  DetailedChapterOutline,
  OpeningContent,
  ExportFormat,
} from '../../../shared/types';

interface ExportButtonProps {
  outline: GeneratedOutline;
  volumeOutline?: VolumeOutline;
  chapterOutline?: DetailedChapterOutline;
  opening?: OpeningContent;
  generatedLevels: ('L1' | 'L2' | 'L3' | 'L4')[];
}

export function ExportButton({
  outline,
  volumeOutline: volumeOutlineProp,
  chapterOutline: chapterOutlineProp,
  opening: openingProp,
  generatedLevels,
}: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [exportedFormat, setExportedFormat] = useState<string | null>(null);

  const handleExport = async (format: ExportFormat, levels: ('L1' | 'L2' | 'L3' | 'L4')[]) => {
    setIsExporting(true);

    try {
      // 调用后端导出 API，直接传入数据
      const apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outline,
          volumeOutline: volumeOutlineProp,
          chapterOutline: chapterOutlineProp,
          opening: openingProp,
          levels,
          formats: [format],
        }),
      });

      const result = await response.json();

      if (result.success && result.files.length > 0) {
        // 下载文件
        for (const file of result.files) {
          downloadFile(file.content, file.filename, getMimeType(file.format));
        }

        setExportedFormat(format);
        setTimeout(() => setExportedFormat(null), 2000);
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('导出失败，请重试');
    } finally {
      setIsExporting(false);
      setShowMenu(false);
    }
  };

  const formatOptions: Array<{
    format: ExportFormat;
    label: string;
    icon: React.ReactNode;
    available: boolean;
  }> = [
    { format: 'md', label: 'Markdown', icon: <FileText size={16} />, available: true },
    { format: 'txt', label: '纯文本', icon: <FileText size={16} />, available: true },
    { format: 'json', label: 'JSON', icon: <FileJson size={16} />, available: true },
    { format: 'svg', label: '思维导图 (SVG)', icon: <Image size={16} />, available: true },
    { format: 'xmind', label: '思维导图 (XMind)', icon: <Share2 size={16} />, available: true },
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={isExporting}
        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
      >
        {isExporting ? (
          <Loader2 size={16} className="animate-spin" />
        ) : exportedFormat ? (
          <Check size={16} />
        ) : (
          <Download size={16} />
        )}
        <span>{isExporting ? '导出中...' : exportedFormat ? '已导出' : '导出'}</span>
      </button>

      <AnimatePresence>
        {showMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50"
          >
            <div className="p-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-700">选择导出格式</p>
              <p className="text-xs text-gray-500 mt-1">
                已生成层级：{generatedLevels.join(' + ')}
              </p>
            </div>

            <div className="p-2">
              {formatOptions.map((option) => (
                <button
                  key={option.format}
                  onClick={() => handleExport(option.format, generatedLevels)}
                  disabled={!option.available || isExporting}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded transition-colors ${
                    option.available
                      ? 'hover:bg-gray-50 text-gray-700'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                >
                  {option.icon}
                  <span className="flex-1">{option.label}</span>
                  {!option.available && (
                    <span className="text-xs text-gray-400">暂不可用</span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-500">
                💡 PDF 格式需要在前端渲染，暂不支持
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ 工具函数 ============

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getMimeType(format: ExportFormat): string {
  const mimeTypes: Record<ExportFormat, string> = {
    txt: 'text/plain',
    md: 'text/markdown',
    json: 'application/json',
    svg: 'image/svg+xml',
    xmind: 'application/json', // XMind JSON 结构
    pdf: 'application/pdf',
  };
  return mimeTypes[format] || 'text/plain';
}
